import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { reportsApi } from '@/api/endpoints/reports';
import { Input } from '@/components/ui/input';
import { formatAddress } from '@/lib/address';
import { cn, formatDateTime } from '@/lib/utils';
import { haversineM } from '@/stores/navRoutes';
import { exportCsv, ReportFilters, ReportShell, ReportTable, ResultBar, useReportFilters } from './_shared';

const KN = 1.94384; // m/s → nós
const MOVING_KN = 0.5; // abaixo disso é considerado parado

interface Seg {
  time: string;        // hora do ponto
  label: string;       // HH:MM para o eixo
  instKn: number;      // velocidade instantânea (rastreador)
  segKn: number;       // velocidade média do trecho (distância/tempo)
  segNm: number;       // distância do trecho (mn)
  address: string;
}

export function SpeedReportPage() {
  const { deviceId, setDeviceId, preset, setPreset, executed, generate } = useReportFilters('single');
  const [limit, setLimit] = useState(20);

  const query = useQuery({
    queryKey: ['report', 'speed', executed],
    queryFn: () => reportsApi.route({ deviceId: executed!.ids[0], from: executed!.range.from, to: executed!.range.to }),
    enabled: executed !== null,
  });

  const { segs, stats } = useMemo(() => {
    const rows = (query.data ?? []).filter((p) => p.latitude != null && p.longitude != null);
    const out: Seg[] = [];
    let maxInst = 0, maxSeg = 0, sumDist = 0, movingTime = 0, sumInst = 0, nMoving = 0;
    for (let i = 0; i < rows.length; i++) {
      const p = rows[i];
      const inst = (p.speed ?? 0); // já em nós (Traccar)
      maxInst = Math.max(maxInst, inst);
      if (inst >= MOVING_KN) { sumInst += inst; nMoving++; }
      let segKn = 0, segNm = 0;
      if (i > 0) {
        const prev = rows[i - 1];
        const m = haversineM({ lat: prev.latitude!, lon: prev.longitude! }, { lat: p.latitude!, lon: p.longitude! });
        const dt = (new Date(p.fixTime).getTime() - new Date(prev.fixTime).getTime()) / 1000;
        segNm = m / 1852;
        if (dt > 0) {
          segKn = (m / dt) * KN;
          sumDist += m;
          if (segKn >= MOVING_KN) movingTime += dt;
        }
        maxSeg = Math.max(maxSeg, segKn);
      }
      const d = new Date(p.fixTime);
      out.push({
        time: formatDateTime(p.fixTime),
        label: `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`,
        instKn: inst, segKn, segNm, address: formatAddress(p.address) || '',
      });
    }
    const totalNm = sumDist / 1852;
    const avgMovingInst = nMoving ? sumInst / nMoving : 0;            // média (instantânea) em movimento
    const avgOverall = movingTime > 0 ? (sumDist / movingTime) * KN : 0; // distância/tempo navegando
    return {
      segs: out,
      stats: { maxInst, maxSeg, avgMovingInst, avgOverall, totalNm, movingMin: Math.round(movingTime / 60), n: rows.length },
    };
  }, [query.data]);

  const chartData = useMemo(
    () => segs.map((s) => ({ label: s.label, vel: Number(s.instKn.toFixed(1)), trecho: Number(s.segKn.toFixed(1)) })),
    [segs],
  );

  const aboveCount = useMemo(
    () => (limit > 0 ? segs.filter((s) => s.instKn > limit).length : 0),
    [segs, limit],
  );

  const handleExport = () => {
    if (!executed) return;
    exportCsv(
      `velocidade_${executed.range.from.slice(0, 10)}_${executed.range.to.slice(0, 10)}.csv`,
      ['Hora', 'Vel. instantânea (nós)', 'Vel. do trecho (nós)', 'Distância do trecho (mn)', 'Endereço'],
      segs.map((s) => [s.time, s.instKn.toFixed(1), s.segKn.toFixed(1), s.segNm.toFixed(3), s.address]),
    );
  };

  const fmt = (v: number) => v.toFixed(1).replace('.', ',');

  return (
    <ReportShell
      title="Velocidade"
      description="Velocidades entre pontos e médias da embarcação no período (em nós)."
    >
      <ReportFilters
        deviceMode="single"
        deviceId={deviceId}
        onDeviceChange={setDeviceId}
        preset={preset}
        onPresetChange={setPreset}
        onGenerate={generate}
        generating={query.isFetching}
      />
      {executed && (
        <ResultBar loading={query.isLoading} count={segs.length} unit="pontos" onExport={handleExport} />
      )}

      {executed && segs.length > 0 && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Kpi label="Vel. máxima" value={`${fmt(stats.maxInst)} nós`} />
            <Kpi label="Vel. média (navegando)" value={`${fmt(stats.avgMovingInst)} nós`} />
            <Kpi label="Vel. média (trajeto)" value={`${fmt(stats.avgOverall)} nós`} />
            <Kpi label="Distância · tempo" value={`${fmt(stats.totalNm)} mn`} sub={`${stats.movingMin} min navegando`} />
          </div>

          {/* Gráfico de velocidade */}
          <div className="rounded-xl border border-border bg-card p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-semibold text-muted-foreground">Velocidade ao longo do tempo (nós)</p>
              <label className="flex items-center gap-1.5 text-xs">
                <span className="text-muted-foreground">Limite (nós)</span>
                <Input type="number" min={0} value={limit} onChange={(e) => setLimit(Math.max(0, Number(e.target.value) || 0))} className="h-7 w-16 text-xs" />
                {limit > 0 && aboveCount > 0 && (
                  <span className="rounded-full bg-status-blocked/10 px-2 py-0.5 font-bold text-status-blocked">{aboveCount} acima</span>
                )}
              </label>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} minTickGap={28} />
                  <YAxis tick={{ fontSize: 10 }} width={36} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    formatter={(v: number, n) => [`${fmt(Number(v))} nós`, n === 'vel' ? 'Instantânea' : 'Trecho']}
                  />
                  {limit > 0 && (
                    <ReferenceLine y={limit} stroke="#FF3B30" strokeDasharray="5 4" label={{ value: `limite ${limit} nós`, fontSize: 10, fill: '#FF3B30', position: 'insideTopRight' }} />
                  )}
                  <Line type="monotone" dataKey="vel" stroke="#007AFF" strokeWidth={2} dot={false} name="vel" />
                  <Line type="monotone" dataKey="trecho" stroke="#FF9500" strokeWidth={1.5} dot={false} name="trecho" strokeDasharray="4 3" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">
              Azul = velocidade do rastreador · Laranja = entre os pontos · Vermelho = limite definido.
            </p>
          </div>
        </>
      )}

      <ReportTable notRun={executed === null} loading={query.isLoading} error={query.error} empty={segs.length === 0}>
        {/* rolagem horizontal: tabela larga nao pode empurrar a pagina inteira no celular */}
        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-secondary/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-2 font-semibold">Hora</th>
              <th className="px-4 py-2 font-semibold">Vel. instantânea</th>
              <th className="px-4 py-2 font-semibold">Vel. trecho</th>
              <th className="px-4 py-2 font-semibold">Distância trecho</th>
              <th className="px-4 py-2 font-semibold">Endereço</th>
            </tr>
          </thead>
          <tbody>
            {segs.slice(0, 1000).map((s, i) => {
              const over = limit > 0 && s.instKn > limit;
              return (
              <tr key={i} className={cn('border-b border-border last:border-0 hover:bg-accent/40', over && 'bg-status-blocked/5')}>
                <td className="whitespace-nowrap px-4 py-2 text-xs">{s.time}</td>
                <td className={cn('px-4 py-2 font-semibold', over && 'text-status-blocked')}>{fmt(s.instKn)} nós{over ? ' ⚠' : ''}</td>
                <td className="px-4 py-2">{i === 0 ? '—' : `${fmt(s.segKn)} nós`}</td>
                <td className="px-4 py-2 text-xs">{i === 0 ? '—' : `${s.segNm.toFixed(3).replace('.', ',')} mn`}</td>
                <td className="px-4 py-2 text-xs">{s.address || '—'}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        {segs.length > 1000 && (
          <p className="border-t border-border p-3 text-center text-xs text-muted-foreground">
            Exibindo primeiros 1000 pontos. Use o CSV para ver tudo.
          </p>
        )}
      </ReportTable>
    </ReportShell>
  );
}

function Kpi({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3">
      <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-lg font-bold leading-tight">{value}</p>
      {sub && <p className="text-[10px] text-muted-foreground">{sub}</p>}
    </div>
  );
}
